import { createStore, scope, TypedNode } from '@rehaje/core'
import { reactive, toJS } from '@rehaje/reactive'

import type { AnyElementSpecific } from '../Element/TypedNode'
import type { QuerySpecific } from '../Query/TypedNode'
import type { VariableSpecific } from '../Variable/TypedNode'
import type { TypedSchema, TypedSpecificLike } from '@rehaje/core'
import type { ReactNode } from 'react'

export interface ComponentSpecific extends TypedSpecificLike {
  elements?: AnyElementSpecific[]
  props?: TypedSchema[]
  queries?: QuerySpecific[]
  type: 'Component'
  variables?: VariableSpecific[]
}

@scope({
  type: 'Block',
  variables: [
    {
      get() {
        return {}
      },
      name: 'props',
      schema() {
        return this.getPropsSchema()
      }
    },
    {
      get() {
        return { $$typeof: Symbol.for('element') }
      },
      name: 'children',
      schema() {
        return {
          type: 'Schema.Element'
        }
      }
    }
  ]
})
export class ComponentNode extends TypedNode {
  @reactive('deep')
  elements: AnyElementSpecific[]

  @reactive('deep')
  props: TypedSchema[]

  @reactive('deep')
  queries: QuerySpecific[]

  @reactive('deep')
  variables: VariableSpecific[]

  get definition() {
    return this.getStore().definition
  }

  constructor(specific: ComponentSpecific) {
    super(specific)
    this.elements = specific.elements ?? []
    this.props = specific.props ?? []
    this.queries = specific.queries ?? []
    this.variables = specific.variables ?? []
  }

  createStore(props?: any, children?: ReactNode) {
    return createStore(
      {
        elements: toJS(this.elements),
        queries: toJS(this.queries),
        type: 'Page',
        variables: toJS(this.variables)
      },
      {
        plugins: this.__r_store.options.plugins,
        variables: {
          children,
          props: this.transformProps(props)
        }
      }
    )
  }

  getPropsSchema(): TypedSchema {
    return {
      properties: this.props,
      type: 'Schema.Object'
    }
  }

  transformProps(props: any) {
    if (this.__r_store.options.disableTypeTransformer) return props
    return this.definition.transformWithSchema(props, this.getPropsSchema())
  }
}

declare global {
  export interface RehajeSpecifics {
    Component: ComponentSpecific
  }
  export interface RehajeTypedNodes {
    Component: ComponentNode
  }
}
